import axios from 'axios';
import FileSaver from 'file-saver';
import jwt from 'jsonwebtoken';
import { S3_CONTAINER, S3_FOLDER_RECORDINGS } from '@config/config';
import axiosService, { axiosFileService } from '@services/axiosService';
import { getNonEmptyPeople } from '@services/selectPeople';
import {
  checkErrors,
  isValidPhoneNumber,
  isValidEmail,
} from '@services/validationService';
import { CONFERENCE } from '@config/modelsConfig';
import rulesCodes from '@config/rules-constants';
import VALIDATION_ERROR_MESSAGES from '@config/validation-messages-constants';
import _ from 'lodash';
import { PERSON_TYPES } from '@config/authentication';

export const conferenceRoutePrefix = '/api/Conferences';

export const upsertConference = (conference, token) =>
  axiosService.post(`${conferenceRoutePrefix}/full?access_token=${token}`, conference);

export const cancelConference = (conferenceId, token) =>
  axiosService.post(
    `${conferenceRoutePrefix}/${conferenceId}/cancel?access_token=${token}`,
  );

export const upsertTranscript = (transcriptData, peopleData, fileNames, token) => {
  const people = getNonEmptyPeople(peopleData);
  const bodydata = {
    ...transcriptData,
    people,
    files: fileNames,
  };
  return axiosService.post(
    `${conferenceRoutePrefix}/full?access_token=${token}`,
    bodydata,
  );
};

export const getConferenceById = ({ id, isAIRoute }, token) =>
  axiosService.get(
    `${conferenceRoutePrefix}/${id}/${
      isAIRoute ? 'full-ai' : 'full'
    }?access_token=${token}`,
  );

const transformPersonResponse = (removeDefaultAttendees) => (
  people,
  { personTypes, chainedUser, ...person },
) => {
  if (removeDefaultAttendees && person.organizationId) {
    return people;
  }
  const transformedPerson = {
    ...person,
    type: personTypes && personTypes.type,
    email: chainedUser && chainedUser.email,
  };
  return [transformedPerson, ...people];
};

export const transformConferenceResponse = (
  { tags, pins, ...conference },
  removeDefaultAttendees,
) => ({
  ...conference,
  people: conference.people
    ? _.orderBy(
        conference.people.reduce(transformPersonResponse(removeDefaultAttendees), []),
        ['id'],
        ['asc'],
      )
    : [],
  tags: tags ? tags.map(({ name }) => name) : [],
  pin: pins && String(pins.pin),
});

export const transformCloudSearchConferenceResponse = ({ score, ...conference }) => {
  const scoreFloat = parseFloat(score) || 0;

  return {
    score: scoreFloat * 10000000,
    ...conference,
  };
};

export const removePin = (conference) => ({ ...conference, pin: null, pinId: null });

export const regeneratePin = (id, token) =>
  axiosService.post(`${conferenceRoutePrefix}/${id}/update-pin?access_token=${token}`);

export const getTranscriptByLinksUUID = ({ uuid, isAIRoute }, token, otp) => {
  const params = {
    access_token: token,
    otp,
  };
  return axiosService.get(`/api/ClientLinks/${uuid}/${isAIRoute ? 'full-ai' : 'full'}`, {
    params,
  });
};

export const uploadFile = (files, token) => {
  const dataFormatted = new FormData();
  // FIXME: Since we send one file only we don't need to accept multiple files here
  files.forEach((file, index) => dataFormatted.append(`file-${index}`, file));
  return axiosFileService.post(`/api/containers/${S3_CONTAINER}/upload`, dataFormatted, {
    params: {
      access_token: token,
      subdirectory: S3_FOLDER_RECORDINGS,
    },
  });
};

export const download = (html, filename, watermark, token = false) => {
  const data = {
    watermark,
    html,
  };
  const config = {
    responseType: 'arraybuffer',
  };
  return axios
    .post(
      `${conferenceRoutePrefix}/download${token ? `?access_token=${token}` : ''}`,
      data,
      config,
    )
    .then((response) => {
      const file = new Blob([response.data], { type: 'application/pdf' });
      FileSaver.saveAs(file, `${filename || 'Third Bridge Connections Transcript'}.pdf`);
    });
};

export const createMediaFile = (conferenceId, token) => {
  return axiosService.post(
    `${conferenceRoutePrefix}/${conferenceId}/create-media-file?access_token=${token}`,
  );
};

export const approveCallAudio = (uuid, token) => {
  return axiosService.post(
    `${conferenceRoutePrefix}/${uuid}/approve-call-audio?access_token=${token}`,
  );
};

export const sendMailToAttendees = (uuid, token) => {
  return axiosService.post(
    `${conferenceRoutePrefix}/${uuid}/send-to-attendees?access_token=${token}`,
  );
};

export const deleteTranscript = (uuid, token) => {
  const data = {
    isArchived: true,
  };
  return axiosService.patch(
    `${conferenceRoutePrefix}/${uuid}?access_token=${token}`,
    data,
  );
};

export const getTranscriptLocale = (conferenceCallUuid, locale, token) =>
  axiosService.get(
    `${conferenceRoutePrefix}?filter=${JSON.stringify({
      fields: ['id'],
      where: { conferenceCallUuid, locale },
      limit: 1,
    })}&access_token=${token}`,
  );

export const decodeUserFromJWT = (encodedJWT) => {
  const token = atob(encodedJWT);
  const tokenData = jwt.decode(token);
  const { user = {} } = tokenData || {};
  return user;
};

export const extractWatermarkData = ({ name, email: emailAddress, type }) =>
  name &&
  emailAddress &&
  type &&
  type.type === PERSON_TYPES.CLIENT && { name, emailAddress };

const generalConferenceValidator = (conference, customValidator) => {
  const errors = {};
  const titleError =
    (!conference.title.trim() && 'Please specify a title.') ||
    (conference.title.trim().length > CONFERENCE.MAX_TITLE_LENGTH &&
      'The title is too long.');
  const organisationError = !conference.organizationId && 'Please select a Client.';

  const validClient = ({ name, type, email }) =>
    name && type === PERSON_TYPES.CLIENT && isValidEmail(email);
  const nameEmpty =
    conference.people.some(({ name }) => !name.trim()) &&
    "The person's name should not be empty.";
  const emailError =
    !conference.people.every(({ email }) => isValidEmail(email)) &&
    'Please specify a valid email address.';
  const clientSpecified = conference.people.some(validClient);
  const clientError =
    !clientSpecified &&
    'Please specify at least one client participant (excl. the default ones).';

  const projectCodeError =
    (!conference.projectCode || !/^[A-Z]{2}-[0-9]{4}$/.test(conference.projectCode)) &&
    'Please specify a valid project code.';

  const specialistNumberError =
    (!conference.specialistNumber || !/^\w+/.test(conference.specialistNumber)) &&
    'Please specify a valid specialist number.';

  checkErrors(
    customValidator(
      {
        titleError,
        organisationError,
        projectCodeError,
        specialistNumberError,
        nameEmpty,
        emailError,
        clientError,
      },
      errors,
    ),
  );
};

const phoneIsNotValid = ({ phoneNumber }) => !isValidPhoneNumber(phoneNumber);

const phoneExistsAndIsNotValid = ({ phoneNumber }) =>
  phoneNumber && !isValidPhoneNumber(phoneNumber);

const getLanguageErrors = (conference) => {
  const languageAIError =
    conference.languagePrefs &&
    !conference.languagePrefs.ai &&
    conference.isAITranscriptNeeded &&
    VALIDATION_ERROR_MESSAGES.notSupportedLanguageAI;

  const languageHumanError =
    conference.languagePrefs &&
    !conference.languagePrefs.human &&
    conference.isTranscriptNeeded &&
    VALIDATION_ERROR_MESSAGES.notSupportedLanguageHuman;

  return {
    languageAIError,
    languageHumanError,
  };
};

const getPhoneNumberErrors = (conference) => {
  const rules = _.get(conference, 'organization.rules', []);
  const isTwoFactorAuthRequiredRule = rules.some(
    ({ code }) => code === rulesCodes.MULTI_FACTOR_AUTHORIZATION,
  );
  const organizationAttendees = _.get(conference, 'organization.attendees', []);
  const conferencePeople = conference.people.concat(organizationAttendees);

  const phoneNumberError =
    (conferencePeople.some(phoneExistsAndIsNotValid) &&
      VALIDATION_ERROR_MESSAGES.notValidPhoneNumber) ||
    (conference.isTranscriptNeeded &&
      conference.isTwoFactorAuthRequired &&
      conferencePeople.some(phoneIsNotValid) &&
      VALIDATION_ERROR_MESSAGES.notAllNumbersAreSpecifiedErrorMessage);

  const notAllNumbersSpecifiedError =
    (isTwoFactorAuthRequiredRule || conference.isTwoFactorAuthRequired) &&
    conferencePeople.some(phoneIsNotValid) &&
    VALIDATION_ERROR_MESSAGES.notAllNumbersAreSpecifiedErrorMessage;

  const countryError =
    conferencePeople.some(({ country }) => !country) &&
    VALIDATION_ERROR_MESSAGES.notValidCountry;

  const conferencePhoneNumberError =
    conferencePeople.some(({ conferencePhoneNumberId }) => !conferencePhoneNumberId) &&
    VALIDATION_ERROR_MESSAGES.notValidConferencePhoneNumber;

  return {
    phoneNumberError,
    notAllNumbersSpecifiedError,
    countryError,
    conferencePhoneNumberError,
  };
};

export const validateTranscript = (conference) =>
  generalConferenceValidator(
    conference,
    ({ titleError, organisationError, nameEmpty, emailError, clientError }, errors) => {
      const { languageAIError, languageHumanError } = getLanguageErrors(conference);

      const audioFileError =
        (!conference.callAudio || conference.callAudio.size === 0) &&
        'Please attach an audio file.';

      const {
        phoneNumberError,
        notAllNumbersSpecifiedError,
        countryError,
        conferencePhoneNumberError,
      } = getPhoneNumberErrors(conference);

      const errorMessage =
        languageHumanError ||
        languageAIError ||
        audioFileError ||
        titleError ||
        organisationError ||
        nameEmpty ||
        phoneNumberError ||
        countryError ||
        conferencePhoneNumberError ||
        emailError ||
        clientError ||
        notAllNumbersSpecifiedError;

      return {
        errorMessage,
        errors,
      };
    },
  );

export const validateConference = (conference) =>
  generalConferenceValidator(
    conference,
    (
      {
        titleError,
        organisationError,
        projectCodeError,
        specialistNumberError,
        nameEmpty,
        emailError,
        clientError,
      },
      errors,
    ) => {
      const { languageAIError, languageHumanError } = getLanguageErrors(conference);

      const {
        phoneNumberError,
        notAllNumbersSpecifiedError,
        countryError,
        conferencePhoneNumberError,
      } = getPhoneNumberErrors(conference);

      const errorMessage =
        languageHumanError ||
        languageAIError ||
        titleError ||
        organisationError ||
        projectCodeError ||
        specialistNumberError ||
        nameEmpty ||
        phoneNumberError ||
        countryError ||
        conferencePhoneNumberError ||
        emailError ||
        clientError ||
        notAllNumbersSpecifiedError;

      return {
        errorMessage,
        errors,
      };
    },
  );

export const callAttendeeFromConference = (token, body) =>
  axiosService.post(
    `/api/Conferences/twilio-call-to-attendee?access_token=${token}`,
    body,
  );

export const requestHumanTranscript = (conferenceId, token) =>
  axiosService.post(
    `/api/Conferences/${conferenceId}/request-manual-transcript/?access_token=${token}`,
  );



// WEBPACK FOOTER //
// ./services/conferenceResource.js